import { Eye as Open16Regular } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { SkyMessageBar, SkyButton } from "@skypointcloud/skypoint-ui";

import { NotifType } from "./Notification.type";
import { INotificationProps } from "./Notification.interface";
import { REMOVE_NOTIFICATION, TIMEOUT_LOW } from "../shared/constant";

const Notification = ({
  type,
  title,
  message,
  timeout,
  dispatch,
  id,
  link,
  linkText,
}: INotificationProps) => {
  const [show, setShow] = useState(false);
  const navigate = useNavigate();

  const closeNotif = () => {
    setShow(false);
    dispatch({
      type: REMOVE_NOTIFICATION,
      id: id,
    });
  };

  const handleActionClick = () => {
    if (link) {
      navigate(link);
      closeNotif();
    }
  };

  const getMessageBarIntent = (type: NotifType): "success" | "error" | "info" | "warning" => {
    switch (type) {
      case "SUCCESS":
        return "success";
      case "ERROR":
        return "error";
      case "INFO":
        return "info";
      case "WARNING":
        return "warning";
      default:
        return "info";
    }
  };

  useEffect(() => {
    setShow(true);

    const autoHideTimeout = timeout || TIMEOUT_LOW;
    if (autoHideTimeout > 0) {
      const timer = setTimeout(() => {
        closeNotif();
      }, autoHideTimeout);
      return () => clearTimeout(timer);
    }
  }, []);

  if (!show) return null;

  return (
    <SkyMessageBar
      intent={getMessageBarIntent(type)}
      title={title}
      message={message}
      // MULTILINE, not the default "auto". Auto measures title + message and only
      // stacks past 80 characters, so a short toast ("New prospect" / "<name> was
      // added to <community>") laid itself out as ONE row: the text column then
      // shares the 400px card with the action button and the dismiss X, and the
      // library truncates it mid-word — the operator never learns which community,
      // which is the only fact the toast carries. Stacking puts the message on its
      // own full-width line and the actions on the row below it.
      layout="multiline"
      className="notification-toast"
      onDismiss={closeNotif}
      actions={
        link ? (
          <SkyButton
            appearance="primary"
            onClick={handleActionClick}
            size="small"
            icon={Open16Regular}
          >
            {linkText || "View"}
          </SkyButton>
        ) : undefined
      }
    />
  );
};

export default Notification;
